import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock3, MapPin, RefreshCw, CalendarDays } from 'lucide-react';
import UpcomingTradeShowsList from './TradeShows/UpcomingTradeShowsList';

// Market Intel's landing dashboard — data-quality counts across every
// Commercial Entity tenant-wide (Customers, Prospects, Suppliers, Manufacturers,
// whatever RelationshipTypeTable holds), sourced from
// /api/commercial-entities/all rather than the Customer-scoped
// /api/customers. Moved here from SalesDashboard.js: Opening Hours/Address
// staleness is a property of any organization-type entity, not a Sales
// concern specifically — see commercialEntityController.js's
// listAllCommercialEntities and bulkRefreshOpeningHours, both already
// tenant-wide with no Customer/Lead scoping.
function MarketIntelDashboard() {
  const navigate = useNavigate();
  const [staleOpeningHours, setStaleOpeningHours] = useState(null);
  const [staleAddress, setStaleAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState('');

  const loadCounts = () => {
    setLoading(true);
    Promise.all([
      axios.get('/api/commercial-entities/all', { params: { opening_hours_stale: 'true', limit: 1 } }),
      axios.get('/api/commercial-entities/all', { params: { address_stale: 'true', limit: 1 } }),
    ])
      .then(([hoursRes, addressRes]) => {
        setStaleOpeningHours(hoursRes.data.pagination?.total ?? 0);
        setStaleAddress(addressRes.data.pagination?.total ?? 0);
      })
      .catch(() => {
        setStaleOpeningHours(0);
        setStaleAddress(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCounts();
  }, []);

  // One combined batch job covers both fields — there's no separate
  // address-only vs hours-only lookup (same Google Places call as the
  // detail page's per-record pills, see OpeningHoursCard.js and
  // commercialEntityController.js's bulkRefreshOpeningHours) — so both
  // cards' Refresh buttons trigger the identical action.
  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshResult('');
    axios.post('/api/commercial-entities/opening-hours/bulk-refresh', null, { params: { limit: 10 } })
      .then((res) => {
        const { processed, found } = res.data;
        setRefreshResult(processed === 0 ? 'No records due for a check.' : `Checked ${processed}, found a match for ${found}.`);
        loadCounts();
      })
      .catch((err) => setRefreshResult(err.response?.data?.message || 'Refresh failed.'))
      .finally(() => setRefreshing(false));
  };

  const cardHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 };
  const refreshBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.3rem 0.6rem', fontSize: '0.78rem' };

  return (
    <div className="dashboard dashboard-page">
      <div className="dashboard-content">
        <div className="dashboard-content-inner">
          <div className="dashboard-page-header">
            <div className="dashboard-page-header-main">
              <h2 className="dashboard-page-title">Market Intel Dashboard</h2>
              <p className="dashboard-page-description">Data quality across every Commercial Entity — Customers, Prospects, Suppliers, and more.</p>
            </div>
          </div>

          <div className="metrics-section">
            <h2>Stale Data</h2>
            <div className="metrics-grid">
              <div className="metric-card">
                <div style={cardHeaderStyle}>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock3 size={16} /> Stale Opening Hours
                  </h3>
                  <button
                    type="button"
                    className="dashboard-refresh-btn"
                    style={refreshBtnStyle}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    title="Look up Opening Hours and Address on Google Places for the most overdue records"
                  >
                    <RefreshCw size={12} className={refreshing ? 'spin' : undefined} />
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
                <div
                  className="metric-value"
                  style={{ fontSize: '1.75rem', cursor: 'pointer' }}
                  onClick={() => navigate('/market-intel-all-entities?opening_hours_stale=true')}
                >
                  {loading ? '—' : staleOpeningHours}
                </div>
              </div>

              <div className="metric-card">
                <div style={cardHeaderStyle}>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MapPin size={16} /> Stale Address
                  </h3>
                  <button
                    type="button"
                    className="dashboard-refresh-btn"
                    style={refreshBtnStyle}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    title="Look up Opening Hours and Address on Google Places for the most overdue records"
                  >
                    <RefreshCw size={12} className={refreshing ? 'spin' : undefined} />
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
                <div
                  className="metric-value"
                  style={{ fontSize: '1.75rem', cursor: 'pointer' }}
                  onClick={() => navigate('/market-intel-all-entities?address_stale=true')}
                >
                  {loading ? '—' : staleAddress}
                </div>
              </div>
            </div>
            {refreshResult && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>{refreshResult}</div>
            )}
          </div>

          <div className="metrics-section">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><CalendarDays size={16} /> Upcoming Trade Shows</h2>
            <div className="metric-card" style={{ maxWidth: 420 }}>
              <UpcomingTradeShowsList limit={5} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MarketIntelDashboard;
